import React, { useState, useEffect, useRef } from "react";
import "./tailwind-preflight.css";
import "./tailwind.css";
import "./Chatbot.css";
import rivalIcon from "./icons/rival-icon.png";
import rivalChatIcon from "./icons/rival-chat-icon.svg";
import rivalAiIcon from "./icons/rival-ai-icon.svg";
import rivalRosiWorkflowIcon from "./icons/rosi-workflow.svg";
import rivalRosiLearnIcon from "./icons/rosi-learn.svg";
import rivalRosiPerformIcon from "./icons/rosi-perform.svg";
import rivalRosiRecruitIcon from "./icons/rosi-recruit.svg";
import rivalTypingIcon from "./icons/rival-typing-icon.svg";
import rivalFeedbackIcon from "./icons/rival-feedback-icon.svg";
import rivalSendIcon from "./icons/rival-send-icon.svg";
import { XMarkIcon, MinusIcon, ArrowsPointingOutIcon, ArrowsPointingInIcon } from "@heroicons/react/24/solid";
import notificationSound from "./notification.mp3";
import { HelpType, greetingMessagesByType, helpPageLinksByType } from "./products";
import Feedback from "./Feedback";
import { formatSource, INPUT_CHARS_LIMIT, PERFORMANCE_CHARS_LIMIT, usePersistentState, getDefaultQuestions, getGreetingMessage, getIconByHelpType } from "./utils";
import Message from "./Message";
import Input from "./Input";

const Chatbot = ({ helpType = HelpType.ONBOARDING, lifeSuiteCode, userRoles = "", rosiPermissionLevel = "admin", managerReviewWriterEnabled = false }) => {
  const [messages, setMessages] = usePersistentState("chatbotMessages", [])
  const [isOpen, setIsOpen] = usePersistentState("chatbotIsOpen", false)
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [isSlidingDown, setIsSlidingDown] = useState(false);
  const [hasChatbotResponded, setHasChatbotResponded] = useState(() => {
    return messages.some(msg => msg.sender === "Rival AI" && !msg.defaultQuestions);
  });
  const [isDocked, setIsDocked] = usePersistentState("chatbotFeedbackIsDocked", true)
  const [isExpanded, setIsExpanded] = usePersistentState("chatbotIsExpanded", false)
  const messagesEndRef = useRef(null);
  const chartMessageRef = useRef(null);
  const isInitialLoad = useRef(true);  // Chat window scroll behavior - Track if it's the initial load
  const notificationAudio = useRef(new Audio(notificationSound));
  const [sessionId, setSessionId] = usePersistentState("chatbotSessionId", null)
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackAvailable, setFeedbackAvailable] = usePersistentState("chatbotFeedbackAvailable", false)

  // Function to detect if a string contains JSON and extract non-null fields as a bulleted list
  const parseJsonToList = (text) => {
    try {
      // Try to find JSON in the text - handle markdown code blocks, arrays, and plain JSON
      let jsonMatch = text.match(/```(?:json)?\s*(\[[\s\S]*?\]|\{[\s\S]*?\})\s*```/);
      if (!jsonMatch) {
        jsonMatch = text.match(/\[[\s\S]*\]|\{[\s\S]*\}/);
      }
      if (!jsonMatch) return null;

      const jsonString = jsonMatch[1] || jsonMatch[0];
      const jsonData = JSON.parse(jsonString);
      
      // Handle specific case where JSON contains nested answer.ul structure
      if (jsonData.answer && jsonData.answer.ul && Array.isArray(jsonData.answer.ul)) {
        const matchedText = jsonMatch[0];
        const remainingText = text.replace(matchedText, '').trim();
        return {
          text: remainingText,
          list: jsonData.answer.ul
        };
      }
      
      // Helper function to format field names for better readability
      const formatFieldName = (fieldName) => {
        return fieldName
          .replace(/([A-Z])/g, ' $1') // Insert space before capital letters (camelCase)
          .replace(/_/g, ' ') // Replace underscores with spaces (snake_case)
          .replace(/\./g, ' → ') // Replace dots with arrows for nested objects
          .replace(/^\w/, c => c.toUpperCase()) // Capitalize first letter
          .trim();
      };
      
      // Extract non-null fields
      const nonNullFields = [];
      
      const extractFields = (obj, prefix = '') => {
        if (Array.isArray(obj)) {
          // Handle arrays of objects
          obj.forEach((item, index) => {
            if (typeof item === 'object' && item !== null) {
              extractFields(item, prefix ? `${prefix}[${index}]` : `Item ${index + 1}`);
            } else if (item !== null && item !== undefined && item !== '') {
              const formattedFieldName = formatFieldName(prefix ? `${prefix}[${index}]` : `Item ${index + 1}`);
              nonNullFields.push(`${formattedFieldName}: ${item}`);
            }
          });
        } else {
          Object.entries(obj).forEach(([key, value]) => {
            const fieldPath = prefix ? `${prefix}.${key}` : key;
            
            if (value !== null && value !== undefined && value !== '') {
              if (typeof value === 'object' && !Array.isArray(value)) {
                // Recursively handle nested objects
                extractFields(value, fieldPath);
              } else if (Array.isArray(value) && value.length > 0) {
                // Handle arrays
                const formattedFieldName = formatFieldName(fieldPath);
                nonNullFields.push(`${formattedFieldName}: ${value.join(', ')}`);
              } else {
                // Handle primitive values
                const formattedFieldName = formatFieldName(fieldPath);
                nonNullFields.push(`${formattedFieldName}: ${value}`);
              }
            }
          });
        }
      };

      extractFields(jsonData);
      
      if (nonNullFields.length > 0) {
        // Return the original text with JSON replaced by a list format
        const matchedText = jsonMatch[0];
        const remainingText = text.replace(matchedText, '').trim();
        return {
          text: remainingText,
          list: nonNullFields
        };
      }
    } catch (error) {
      // Not valid JSON, return null
      return null;
    }
    
    return null;
  };

  // Preload icons
  useEffect(() => {
    const icons = [rivalIcon, rivalChatIcon, rivalAiIcon, rivalRosiWorkflowIcon, rivalRosiLearnIcon, rivalRosiPerformIcon, rivalRosiRecruitIcon, rivalSendIcon, rivalTypingIcon];
    icons.forEach((icon) => {
      const ico = new Image();
      ico.src = icon;
    });
  }, []);

  useEffect(() => {
    if (!hasChatbotResponded) {
      const defaultQuestions = getDefaultQuestions(helpType, userRoles, rosiPermissionLevel, managerReviewWriterEnabled);
      const greetingMessage = getGreetingMessage(helpType, rosiPermissionLevel, greetingMessagesByType);
      
      const initialMessage = {
        sender: "Rival AI",
        text: greetingMessage,
        defaultQuestions,
      };
      setMessages([initialMessage]);
    }
  }, [helpType, userRoles, rosiPermissionLevel, isOpen, hasChatbotResponded, setMessages, managerReviewWriterEnabled]);

  // Scroll to bottom on message update or open
  useEffect(() => {
    if (isOpen && messagesEndRef.current) {
      const behavior = isInitialLoad.current ? 'instant' : 'smooth';

      // If the latest message has chart data, scroll to the top of that message
      const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
      const scrollTarget = lastMessage?.tableData && chartMessageRef.current
        ? chartMessageRef.current
        : messagesEndRef.current;
      const scrollBlock = lastMessage?.tableData && chartMessageRef.current ? 'start' : 'end';

      // Watch for changes in the messages container's size
      const resizeObserver = new ResizeObserver(() => {
        scrollTarget.scrollIntoView({ behavior, block: scrollBlock });

        if (isInitialLoad.current) {
          isInitialLoad.current = false;  // Reset after the first scroll
        }
      });

      const parentElement = messagesEndRef.current.parentElement;
      if (parentElement) {
        resizeObserver.observe(parentElement);
      }

      // Cleanup observer when component unmounts or dependencies change
      return () => {
        if (parentElement) {
          resizeObserver.unobserve(parentElement);
        }
      };
    }
  }, [messages, isOpen]);

  const charLimit = helpType === HelpType.PERFORM ? PERFORMANCE_CHARS_LIMIT : INPUT_CHARS_LIMIT;

  // Handling sending a message or question
  const handleSend = async (query = null) => {
    if (query.trim() === "" || query.length > charLimit) return;

    if (!hasChatbotResponded) {
      setHasChatbotResponded(true);
    }
    const newMessages = [
      ...messages,
      { sender: "endUser", text: query },
      { sender: "Rival AI", typing: true },
    ];
    setMessages(newMessages);
    setIsLoading(true);

    try {
      let endpoint;
      switch (helpType) {
        case HelpType.ONBOARDING:
          endpoint = 'ask-knowledge-agent';
          break;
        case HelpType.PERFORM:
          endpoint = 'ask-knowledge-agent-for-perform';
          break;
        default:
          endpoint = 'ask-chatbot';
      }

      const headers = {
        'Content-Type': 'application/json',
        'X-Website-Domain': window.location.origin,
      };

      const body = {
        question: query,
        help_type: helpType,
        session_id: sessionId,
        lifesuite_code: lifeSuiteCode,
      };

      if (helpType === HelpType.PERFORM) {
        body.manager_review = managerReviewWriterEnabled;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/${endpoint}`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorData = await response.json();
        const msg = errorData.error || errorData.error_message;
        if (msg) {
          handleError(msg);
          return;
        }
        throw new Error('There was an issue with the network response');
      }

      const data = await response.json();
      handleAnswer(data);
    } catch (error) {
      console.error('Error:', error);
      handleError();
    }
  };

  // Helper function to convert newlines to HTML line breaks
  const convertNewlinesToBr = (text) => {
    if (typeof text !== 'string') return text;
    return text.replace(/\n/g, '<br>');
  };

  // Handling the response from the AI - Message formatting
  const handleAnswer = (value) => {
    setIsLoading(false);
    setMessages((prevMessages) => prevMessages.filter((message) => !message.typing));

    // Handle "No Answer" case by calling handleError
    if (value.answer === "No Answer") {
      handleError();
      return;
    }

    const createMessage = () => {
      if (typeof value.answer === "string") {
        // Try to parse JSON from the string response
        const jsonParsed = parseJsonToList(value.answer);
        if (jsonParsed) {
          return { 
            sender: "Rival AI", 
            text: convertNewlinesToBr(jsonParsed.text || ""), 
            list: jsonParsed.list 
          };
        }
        return { sender: "Rival AI", text: convertNewlinesToBr(value.answer) };
      }
      if (Array.isArray(value.answer)) {
        // Check each array item for JSON and parse if found
        const processedAnswers = [];
        let foundJson = false;
        let combinedList = [];
        
        value.answer.forEach(item => {
          const jsonParsed = parseJsonToList(item);
          if (jsonParsed) {
            foundJson = true;
            if (jsonParsed.text) processedAnswers.push(jsonParsed.text);
            combinedList = combinedList.concat(jsonParsed.list);
          } else {
            processedAnswers.push(item);
          }
        });
        
        if (foundJson) {
          return { 
            sender: "Rival AI", 
            text: convertNewlinesToBr(processedAnswers.join("\n\n")),
            list: combinedList
          };
        }
        
        return { sender: "Rival AI", text: convertNewlinesToBr(value.answer.join("\n\n")) };
      }
      if (value.answer.ul && Array.isArray(value.answer.ul)) {
        return { sender: "Rival AI", list: value.answer.ul };
      }
      // Handle case where the entire answer is a JSON object
      if (typeof value.answer === "object" && value.answer !== null) {
        const nonNullFields = [];
        
        // Helper function to format field names for better readability
        const formatFieldName = (fieldName) => {
          return fieldName
            .replace(/([A-Z])/g, ' $1') // Insert space before capital letters (camelCase)
            .replace(/_/g, ' ') // Replace underscores with spaces (snake_case)
            .replace(/\./g, ' → ') // Replace dots with arrows for nested objects
            .replace(/^\w/, c => c.toUpperCase()) // Capitalize first letter
            .trim();
        };
        
        const extractFields = (obj, prefix = '') => {
          Object.entries(obj).forEach(([key, value]) => {
            const fieldPath = prefix ? `${prefix}.${key}` : key;
            
            if (value !== null && value !== undefined && value !== '') {
              if (typeof value === 'object' && !Array.isArray(value)) {
                extractFields(value, fieldPath);
              } else if (Array.isArray(value) && value.length > 0) {
                const formattedFieldName = formatFieldName(fieldPath);
                nonNullFields.push(`${formattedFieldName}: ${value.join(', ')}`);
              } else {
                const formattedFieldName = formatFieldName(fieldPath);
                nonNullFields.push(`${formattedFieldName}: ${value}`);
              }
            }
          });
        };

        extractFields(value.answer);
        
        if (nonNullFields.length > 0) {
          return { sender: "Rival AI", list: nonNullFields };
        }
      }
      return {
        sender: "Rival AI",
        text: "I apologize, but I was unable to parse the response format. Please try again later."
      };
    };

    const newMessage = createMessage();

    if (value.source) {
      const sourceText = formatSource(value.source);
      if (newMessage.list) {
        newMessage.text = convertNewlinesToBr(sourceText);
      } else {
        newMessage.text = newMessage.text ? convertNewlinesToBr(`${newMessage.text}\n\n${sourceText}`) : convertNewlinesToBr(sourceText);
      }
    }

    if (value.review) {
      newMessage.review = true;
    }

    if (value.evidence) {
      newMessage.evidence = value.evidence;
    }

    if (value.table_data) {
      newMessage.tableData = value.table_data;
      setIsExpanded(true);
    }

    if (value.summary) {
      newMessage.responseSummary = value.summary;
    }

    setFeedbackAvailable(true);
    setSessionId(value.session_id);
    setMessages((prevMessages) => {
      const updatedMessages = [...prevMessages, newMessage];
      notificationAudio.current.play();
      return updatedMessages;
    });
  };

  // Handling errors
  const handleError = (customMessage) => {
    setIsLoading(false);
    setMessages((prevMessages) =>
      prevMessages.filter((message) => !message.typing)
    );

    if (customMessage) {
      setMessages((prevMessages) => [
        ...prevMessages,
        { sender: "Rival AI", text: customMessage },
      ]);
      notificationAudio.current.play();
      return;
    }

    const helpPageLink = helpPageLinksByType[helpType] || "https://workflow-help.rival-hr.com/";

    setMessages((prevMessages) => [
      ...prevMessages,
      {
        sender: "Rival AI",
        text: `
                <div class="tw-whitespace-normal">
                  <p>Hmm... I’m having trouble finding the answer. I get better all the time, so thanks for your patience!</p>
                  <p>You could also visit our <a href="${helpPageLink}" target="_blank" rel="noopener noreferrer">Help page</a> or contact us at <a href="mailto:Support@rival-hr.com">Support@rival-hr.com</a>.</p>
                </div>
              `,
      },
    ]);
    notificationAudio.current.play();
  };

  // Confirm closing the chat session
  const confirmClose = () => {
    setIsOpen(false);
    setShowConfirmClose(false);
    setMessages([]);  // Clear messages in state
    setHasChatbotResponded(false);  // Reset chatbot responded state
    setFeedbackAvailable(false);  // Reset feedback bubble visibility
    setIsDocked(false);  // Reset docked state
    setIsExpanded(false);  // Reset expanded state
    setSessionId(null)
  };

  // Cancel closing the chat session
  const cancelClose = () => {
    setIsSlidingDown(true);
    setTimeout(() => {
      setShowConfirmClose(false);
      setIsSlidingDown(false);
    }, 300);
  };

  // Toggle expanded state
  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };


  useEffect(() => {
    const updateFeedbackPosition = () => {
      const chatInputContainer = document.querySelector('.chat-input-container');
      const feedbackContainer = document.querySelector('.feedback-container');

      if (chatInputContainer && feedbackContainer) {
        const chatInputHeight = chatInputContainer.offsetHeight;
        const bottomPadding = 0; // in px
        feedbackContainer.style.bottom = `${chatInputHeight + bottomPadding}px`;
      }
    };

    // Function to lock the feedback container position
    const lockFeedbackPosition = () => {
      const feedbackContainer = document.querySelector('.feedback-container');
      if (feedbackContainer) {
        const currentBottom = feedbackContainer.style.bottom;
        feedbackContainer.style.position = 'fixed'; // Lock position
        feedbackContainer.style.bottom = currentBottom;
      }
    };

    // Update position on initial load
    setTimeout(updateFeedbackPosition, 0);

    // Update position when messages change, chat is minimized/opened
    updateFeedbackPosition();

    // Monitor chat input container for size changes
    const chatInputContainer = document.querySelector('.chat-input-container');
    if (chatInputContainer) {
      const resizeObserver = new ResizeObserver(() => {
        updateFeedbackPosition();
      });
      resizeObserver.observe(chatInputContainer);

      return () => {
        resizeObserver.disconnect();
      };
    }

    // Recalculate on window resize
    window.addEventListener('resize', updateFeedbackPosition);

    // Lock feedback position when modals are shown
    const showModalElements = [showConfirmClose, showFeedbackModal];
    if (showModalElements.some(Boolean)) {
      lockFeedbackPosition();
    }

    return () => {
      window.removeEventListener('resize', updateFeedbackPosition);
    };
  }, [messages, isOpen, showConfirmClose, showFeedbackModal]);

  return (
    <div className="chatbot-container tw-fixed tw-bottom-4 tw-right-4">
      {isOpen ? (
        <div
          id="chat-window-container"
          className={`tw-flex tw-m-0 tw-p-2 tw-rounded tw-shadow-lg tw-backdrop-blur-sm tw-transition-all tw-transform tw-duration-300 ${
            isExpanded ? 'expanded' : ''
          }`}
        >
          <div
            id="chat-window"
            className={`tw-rounded tw-bg-white tw-flex tw-flex-col ${showConfirmClose || showFeedbackModal ? "grayed-out" : ""} ${
              isExpanded ? 'expanded' : ''
            }`}
          >
            <div className="tw-p-4 tw-rounded-t-lg tw-flex tw-justify-start tw-items-center tw-text-black">
              <img
                src={getIconByHelpType(helpType)}
                alt="ROSI"
                className="tw-block tw-w-9 tw-h-9 tw-rounded-full tw-border tw-border-gray-300 tw-mr-3"
              />
              <span className="tw-grow tw-text-lg tw-font-semibold tw-tracking-tight">
                {"ROSI"}
              </span>
              {isExpanded ? (
                <ArrowsPointingInIcon
                  onClick={toggleExpanded}
                  className="tw-w-8 tw-h-8 tw-p-1 tw-cursor-pointer hover:tw-bg-gray-100 hover:tw-rounded"
                  title="Minimize"
                />
              ) : (
                <ArrowsPointingOutIcon
                  onClick={toggleExpanded}
                  className="tw-w-8 tw-h-8 tw-p-1 tw-cursor-pointer hover:tw-bg-gray-100 hover:tw-rounded"
                  title="Expand"
                />
              )}
              <MinusIcon
                onClick={() => {
                  setIsOpen(false);
                  setIsExpanded(false);
                }}
                className="tw-w-8 tw-h-8 tw-p-1 tw-cursor-pointer hover:tw-bg-gray-100 hover:tw-rounded"
                title="Minimize"
              />
              <XMarkIcon
                onClick={() => setShowConfirmClose(true)}
                className="tw-w-8 tw-h-8 tw-p-1 tw-cursor-pointer hover:tw-bg-gray-100 hover:tw-rounded"
                title="Close"
              />
            </div>
            <div className="tw-flex-1 tw-p-4 tw-pb-0 tw-overflow-y-auto custom-scroll">
              {messages.map((message, index) => {
                const isLastChartMessage = message.tableData && index === messages.length - 1;
                return (
                  <div key={index} ref={isLastChartMessage ? chartMessageRef : undefined}>
                    <Message
                      sender={message.sender}
                      text={message.text}
                      typing={message.typing}
                      list={message.list}
                      url={message.url}
                      defaultQuestions={message.defaultQuestions}
                      handleSend={handleSend}
                      isLoading={isLoading}
                      helpType={helpType}
                      review={message.review}
                      evidence={message.evidence}
                      tableData={message.tableData}
                      responseSummary={message.responseSummary}
                      isExpanded={isExpanded}
                      onRequestExpand={() => setIsExpanded(true)}
                    />
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {feedbackAvailable && !isExpanded && (
              <div
                className={`feedback-container animate-slide-in tw-absolute tw-left-6 tw-flex tw-items-center tw-rounded-lg tw-rounded-bl-none tw-bg-white tw-border tw-border-solid tw-border-notblurple tw-py-0 ${isDocked ? 'tw-px-1.5' : 'tw-px-2'}`}
              >
                <div
                  className={`feedback-link-container tw-py-1 tw-text-rivalsoftblack tw-overflow-hidden tw-text-xs tw-relative tw-flex tw-items-center tw-cursor-pointer hover:tw-underline hover:tw-underline-offset-3`}
                  style={{
                    maxWidth: isDocked ? '0px' : '250px',
                    transition: 'max-width 0.3s cubic-bezier(0.4, 0, 0.2, 1)', // Smoother transition curve
                  }}
                  onClick={() => setShowFeedbackModal(true)}
                >
                  <span
                    className={`tw-mr-1 tw-transition-translate tw-duration-300 tw-ease-in-out tw-whitespace-nowrap`}
                    style={{
                      transform: isDocked ? 'translateX(-100%)' : 'translateX(0)',
                    }}
                  >
                    Feedback
                  </span>
                </div>
                <div className="tw-flex tw-justify-center tw-items-center tw-py-1 tw-text-notblurple tw-cursor-pointer chevron-container">
                  {isDocked ? (
                    <img
                      src={rivalFeedbackIcon}
                      onClick={() => setIsDocked(false)}
                      alt="Feedback Icon"
                      className="tw-w-5 tw-h-5 tw-transition-transform tw-duration-300 hover:tw-scale-110"
                    />
                  ) : (
                    <XMarkIcon
                      onClick={() => setIsDocked(true)}
                      className="tw-w-5 tw-h-5 tw-transform tw-transition-transform tw-duration-300 hover:tw-scale-110"
                    />
                  )}
                </div>
              </div>
            )}

            <Input handleSend={handleSend} isLoading={isLoading} helpType={helpType} />
          </div>
        </div>
      ) : (
        <div id="rival-copilot-icon" className="tw-text-notblurple tw-rounded-full tw-bg-white tw-border-0">
          <img
            src={getIconByHelpType(helpType)}
            onClick={() => setIsOpen(true)}
            alt="ROSI Chat Icon"
            className="tw-block tw-cursor-pointer tw-w-16 tw-h-16 tw-p-3"
          />
        </div>
      )}

      {showConfirmClose && (
        <div className={`confirm-close-modal tw-fixed tw-bottom-6 tw-bg-white tw-p-4 tw-rounded tw-shadow-lg tw-backdrop-blur-sm ${isSlidingDown ? "slide-down" : ""}`} style={{ zIndex: 10001 }}>
          <h1 className="tw-grow tw-text-lg tw-font-semibold tw-tracking-tight tw-text-center tw-m-0 tw-p-0 tw-mb-2">
            End Chat
          </h1>
          <p className="tw-text-sm tw-font-normal tw-m-0 tw-p-0 tw-text-center">
            Are you sure you want to end the current chat session?
          </p>
          <div className="tw-flex tw-justify-center tw-mt-5">
            <button
              onClick={confirmClose}
              className="tw-bg-notblurple tw-border-0 tw-text-sm tw-text-white tw-px-3 tw-py-2 tw-rounded tw-mr-3 hover:tw-opacity-90"
            >
              End Chat
            </button>
            <button
              onClick={cancelClose}
              className="tw-bg-rivalcharcoal tw-border-0 tw-text-sm tw-text-white tw-px-3 tw-py-2 tw-rounded hover:tw-opacity-90"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {showFeedbackModal && (
        <Feedback sessionId={sessionId} setMessages={setMessages} setShowFeedbackModal={setShowFeedbackModal} setFeedbackAvailable={setFeedbackAvailable} />
      )}
    </div>
  );
};

export default Chatbot;
